import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type {
  PositionStaffingPeoplePage,
  PositionStaffingPersonRow,
} from '../models/PositionStaffingIntent';

const ATLAS_PEOPLE_PAGE_SIZE = 50;
/**
 * The roster projection is a multi-page permissioned read, so a cold page can
 * legitimately run for several seconds. Bound it anyway: without a ceiling a
 * stalled request leaves the picker on "Searching authorized People…" with no
 * way out, which reads as a broken feature rather than a slow one.
 */
const ATLAS_PEOPLE_TIMEOUT_MS = 25_000;
/**
 * One retry, not the default three. Each attempt can occupy the full timeout
 * above, and the query stays `pending` for every one of them — three silent
 * retries kept the picker spinning past a minute before it surfaced an error.
 */
const ATLAS_PEOPLE_RETRIES = 1;

export const atlasPeopleSearchKey = (
  folderId: string,
  diagramId: string,
  search: string,
) => ['atlas', 'staffing-people', folderId, diagramId, search] as const;

/**
 * One permissioned, cursor-backed People lookup for Atlas authoring surfaces.
 *
 * The endpoint owns protected projection and omission semantics. Consumers may
 * change the presentation (staffing list or succession picker), but must not
 * rebuild a People roster from canvas nodes or infer omitted candidates.
 */
export function useAtlasPeopleSearch(input: {
  folderId?: string | null;
  diagramId?: string | null;
  search: string;
  enabled: boolean;
}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const normalizedSearch = input.search.trim();
  const [deferredSearch, setDeferredSearch] = useState(normalizedSearch);
  useEffect(() => {
    const timer = window.setTimeout(() => setDeferredSearch(normalizedSearch), 250);
    return () => window.clearTimeout(timer);
  }, [normalizedSearch]);
  const searchPending = normalizedSearch !== deferredSearch;
  const query = useInfiniteQuery({
    queryKey: atlasPeopleSearchKey(folderId, diagramId, deferredSearch),
    enabled: input.enabled && Boolean(folderId && diagramId),
    initialPageParam: null as string | null,
    retry: ATLAS_PEOPLE_RETRIES,
    queryFn: async ({ pageParam, signal }): Promise<PositionStaffingPeoplePage> => {
      const params = new URLSearchParams({ limit: String(ATLAS_PEOPLE_PAGE_SIZE) });
      if (deferredSearch) params.set('search', deferredSearch);
      if (pageParam) params.set('cursor', pageParam);
      const deadline = new AbortController();
      let timedOut = false;
      const timer = window.setTimeout(() => {
        timedOut = true;
        deadline.abort();
      }, ATLAS_PEOPLE_TIMEOUT_MS);
      // React Query cancels through `signal` when the debounced term moves on;
      // forward that so a superseded search stops work instead of racing.
      const forwardCancel = () => deadline.abort();
      signal.addEventListener('abort', forwardCancel);
      try {
        const response = await authenticatedFetch(
          `/api/folders/${folderId}/diagrams/${diagramId}/atlas/staffing-people?${params}`,
          { signal: deadline.signal },
        );
        if (!response.ok) throw new Error(`Atlas People query failed (${response.status})`);
        return response.json() as Promise<PositionStaffingPeoplePage>;
      } catch (error) {
        // A caller-driven cancel must stay a cancel so React Query discards it
        // quietly; only our own deadline becomes a surfaced failure.
        if (timedOut && !signal.aborted) {
          throw new Error(`Atlas People query timed out after ${ATLAS_PEOPLE_TIMEOUT_MS}ms`);
        }
        throw error;
      } finally {
        window.clearTimeout(timer);
        signal.removeEventListener('abort', forwardCancel);
      }
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

  const rows = useMemo(() => {
    const unique = new Map<string, PositionStaffingPersonRow>();
    for (const page of query.data?.pages ?? []) {
      for (const row of page.rows) {
        if (!unique.has(row.personId)) unique.set(row.personId, row);
      }
    }
    return [...unique.values()];
  }, [query.data]);

  return {
    ...query,
    // Do not render rows from the previous query while a new term is waiting
    // for its debounce boundary; that made stale People selectable.
    rows: searchPending ? [] : rows,
    isLoading: query.isLoading || searchPending,
    deferredSearch,
    searchPending,
  };
}
